// app/(app)/products/Products.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';
import {
  collection,
  addDoc,
  doc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import { db, storage } from '@root/firebase';
import {
  deleteObject,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';

import Layout from '@components/Layout';
import Modal from '@/components/Modal';
import ImageUploader from '@components/ImageUploader'; // ⛔ keep as-is (Dropzone remains internal)
import MarketplaceMenu from '@components/MarketplaceMenu';
import TagInput from '@components/TagInput';

import { toast } from 'react-hot-toast';
import { useAuth } from '@context/AuthContext';

import { dogBreeds } from '@utils/dogBreeds';
import { normalizeBreeds } from '@/utils/normalize';
import { BREED_ALL } from '@/constants/breeds';
import { GEO_OPTIONS } from '@/constants/geography';
import { withUTM } from '@/utils/url';
import { validateProductInput } from '@/utils/validateProduct';
import {
  replaceSubcollection,
  writeSubsMeta,
  readSubsMetaNames,
  deleteSubcollection,
} from '@/utils/firestoreSubcollections';

import useCategories from '@/hooks/useCategories';
import useProducts from '@/hooks/useProducts';
import SearchableMultiSelect from '@/components/SearchableMultiSelect';
import ProductCard from '@/components/ProductCard';

/* =====================================================================================
   Focus Keeper — preserve focus/caret across remounts (modal/step/list changes)
   ===================================================================================== */
function useFocusKeeper() {
  const containerRef = useRef(null);
  const last = useRef({ id: null, pos: null });

  const onFocusCapture = (e) => {
    const el = e.target;
    if (!el) return;
    const isTextInput =
      el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
    if (!isTextInput) return;
    const id = el.getAttribute('data-focus-id');
    if (!id) return;

    let pos = null;
    try {
      pos = typeof el.selectionStart === 'number' ? el.selectionStart : null;
    } catch {}
    last.current = { id, pos };
  };

  const restore = () => {
    const root = containerRef.current;
    if (!root) return;

    const active = document.activeElement;
    if (active && root.contains(active)) return;

    const { id, pos } = last.current || {};
    if (!id) return;

    const el = root.querySelector(`[data-focus-id="${id}"]`);
    if (!el) return;

    el.focus?.({ preventScroll: true });
    try {
      if (typeof pos === 'number' && el.setSelectionRange) {
        el.setSelectionRange(pos, pos);
      }
    } catch {}
  };

  return { containerRef, onFocusCapture, restore };
}

/* =====================================================================================
   Freeze — never re-render children (protects Dropzone internal preview state)
   ===================================================================================== */
const Freeze = React.memo(
  ({ children }) => children,
  () => true
);

/* =====================================================================================
   URL helpers for evidence normalization
   ===================================================================================== */
const looksLikeUrl = (s) => /\.[a-z]{2,}/i.test(String(s || '').trim());
const normalizeUrl = (s) => {
  const v = String(s || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return looksLikeUrl(v) ? `https://${v}` : v;
};

/* =====================================================================================
   Products
   ===================================================================================== */

function Products() {
  const { globalData, globalUser } = useAuth();

  // categories
  const { categories, catMap, categoryTree } = useCategories();
  const subCategoriesFor = (parentId) =>
    categories.filter((c) => c.parent === parentId);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');

  // products hook (list + paging)
  const {
    products,
    loading,
    lastVisible,
    fetchProducts,
    fetchMore,
    setProducts,
    setLoading,
  } = useProducts(db, catMap);

  // deletion/save modal state
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteId, setDeleteId] = useState('');
  const [deleteAvatar, setDeleteAvatar] = useState('');
  const [deleteName, setDeleteName] = useState('');

  // geography
  const [selectedGeo, setSelectedGeo] = useState([]);
  const toggleGeo = (label) =>
    setSelectedGeo((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label]
    );

  // form (prepopulated defaults for testing)
  const [product, setProduct] = useState('Test Dog Jacket');
  const [subtitle, setSubtitle] = useState('Warm waterproof winter coat');
  const [brand, setBrand] = useState('Paws & Co.');
  const [price, setPrice] = useState('49.99');
  const [description, setDescription] = useState(
    'A cosy, waterproof jacket for medium to large breeds. Adjustable straps and reflective trim.'
  );
  const [externalUrl, setExternalUrl] = useState('https://www.google.com');
  const [tags, setTags] = useState([]);

  // breeds
  const BREEDS = useMemo(() => normalizeBreeds(dogBreeds), []);
  const [selectedBreeds, setSelectedBreeds] = useState([BREED_ALL]);

  /* ---------------- Evidence (typed rows with stable IDs) ---------------- */
  const EVIDENCE_TYPES = [
    'Manufacturer Certificate',
    'Vet Approval',
    'Lab Test / Analysis',
    'Receipt / Invoice',
    'Safety Compliance (CE/UKCA)',
    'Warranty / Serial Proof',
    'Official Product Page',
    'Batch / LOT Traceability',
    'Other',
  ];

  const makeRowId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // rows: [{ id, type, url }]
  const [evidenceRows, setEvidenceRows] = useState([
    {
      id: makeRowId(),
      type: 'Official Product Page',
      url: 'https://example.com/product',
    },
  ]);

  const addEvidenceRow = useCallback(() => {
    setEvidenceRows((rows) => [
      ...rows,
      { id: makeRowId(), type: '', url: '' },
    ]);
  }, []);

  const removeEvidenceRow = useCallback((id) => {
    setEvidenceRows((rows) => rows.filter((r) => r.id !== id));
  }, []);

  const updateEvidenceRow = useCallback((id, patch) => {
    setEvidenceRows((rows) =>
      rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
  }, []);

  // wizard
  const [step, setStep] = useState(1);
  const maxStep = 4;
  const goNext = () => setStep((s) => Math.min(s + 1, maxStep));
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  // ImageUploader (Dropzone) must remain internal to this file
  const imageUploaderRef = useRef(null);

  // Mirror selected file without re-rendering (prevents preview loss)
  const selectedFileRef = useRef(null);
  const handleFileSelect = useCallback((file) => {
    selectedFileRef.current = file ?? null; // no setState => no re-render
  }, []);
  const handleUploadNoop = useCallback(() => {}, []);

  // Build ONE uploader element and freeze it so it never re-renders or remounts
  const uploaderEl = useMemo(
    () => (
      <ImageUploader
        ref={imageUploaderRef}
        uid={globalData.uid}
        onFileSelect={handleFileSelect} // stable
        onUpload={handleUploadNoop} // stable
      />
    ),
    [globalData.uid, handleFileSelect, handleUploadNoop]
  );

  /* ---------- effects ---------- */
  useEffect(() => {
    if (Object.keys(catMap).length) fetchProducts();
  }, [catMap, fetchProducts]);

  /* ---------- Focus Keeper ---------- */
  const focus = useFocusKeeper();
  useLayoutEffect(() => {
    focus.restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    step,
    product,
    subtitle,
    brand,
    price,
    description,
    externalUrl,
    evidenceRows,
    selectedGeo.length,
    selectedBreeds.join('|'),
  ]);

  /* ---------- UI helpers ---------- */
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory('');
  };

  // per-step lightweight validation to guide user before Save
  const validateStep = (s) => {
    if (s === 1) {
      if (!product?.trim()) return 'Product name is required.';
      if (!selectedCategory) return 'Category is required.';
      if (!selectedSubCategory) return 'Sub-category is required.';
    }
    if (s === 3) {
      if (!price || isNaN(price) || parseFloat(price) <= 0)
        return 'Valid price is required.';
      if (!description?.trim()) return 'Description is required.';
      if (!externalUrl?.trim()) return 'External URL is required.';
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep(step);
    if (err) return toast.error(err);
    goNext();
  };

  const handleOpenAdd = () => {
    setStep(1);
    setProduct('Test Dog Jacket');
    setSubtitle('Warm waterproof winter coat');
    setBrand('Paws & Co.');
    setPrice('49.99');
    setDescription(
      'A cosy, waterproof jacket for medium to large breeds. Adjustable straps and reflective trim.'
    );
    setSelectedCategory('');
    setSelectedSubCategory('');
    setExternalUrl('https://www.google.com');
    setTags([]);
    setSelectedBreeds([BREED_ALL]);
    setSelectedGeo([]);
    setEvidenceRows([
      {
        id: makeRowId(),
        type: 'Official Product Page',
        url: 'https://example.com/product',
      },
    ]);
    selectedFileRef.current = null; // do not set state => no re-render
  };

  /* ---------- Save ---------- */
  const handleSaveProduct = async () => {
    const err = validateProductInput({
      product,
      price,
      description,
      selectedCategory,
      selectedSubCategory,
      externalUrl,
    });
    if (err) {
      toast.error(err);
      return;
    }
    if (!imageUploaderRef.current) {
      toast.error('ImageUploader not ready.');
      return;
    }

    setLoading(true);
    try {
      // Prefer the ref; if the component exposes a getter, use it as a fallback
      const fileFromGetter =
        typeof imageUploaderRef.current?.getSelectedFile === 'function'
          ? imageUploaderRef.current.getSelectedFile()
          : null;
      const fileToUpload = selectedFileRef.current || fileFromGetter || null;

      let uploadedImage = null;
      if (fileToUpload) {
        const rand =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const originalName = fileToUpload.name || `${product}.jpg`;
        const path = `users/${globalData.uid}/marketplace/${rand}-${originalName}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, fileToUpload);
        uploadedImage = await getDownloadURL(fileRef);
      }

      const breedsToSave =
        selectedBreeds?.[0] === BREED_ALL ? [BREED_ALL] : [...selectedBreeds];
      const geoToSave = Array.isArray(selectedGeo) ? selectedGeo : [];
      const tagsToSave = Array.isArray(tags) ? tags : [];

      const rootDoc = {
        type: 'products',
        product: product.trim(),
        subtitle: subtitle.trim() || null,
        brand: brand.trim() || null,
        price: parseFloat(price),
        description: description.trim(),
        categoryId: selectedCategory,
        subCategoryId: selectedSubCategory,
        avatar: uploadedImage,
        uid: globalData.uid,
        externalUrl: withUTM(externalUrl, 'smartdog', 'marketplace'),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        status: 'active',
      };

      const collRef = collection(db, 'marketplace');
      const docRef = await addDoc(collRef, rootDoc);

      // Save subcollections (await each group)
      await Promise.all([
        replaceSubcollection(
          db,
          `marketplace/${docRef.id}`,
          'breeds',
          breedsToSave
        ),
        replaceSubcollection(
          db,
          `marketplace/${docRef.id}`,
          'geography',
          geoToSave
        ),
        replaceSubcollection(
          db,
          `marketplace/${docRef.id}`,
          'tags',
          tagsToSave
        ),
      ]);

      // --- Evidence (normalize + validate + write) ---
      const rawEvidence = Array.isArray(evidenceRows) ? evidenceRows : [];
      const normalizedEvidence = rawEvidence
        .map(({ type, url }) => ({
          type: String(type || '').trim(),
          url: normalizeUrl(url),
          originalUrl: String(url || '').trim(),
        }))
        .filter(({ url }) => !!url && /^https?:\/\//i.test(url));

      const dropped = rawEvidence.filter(
        ({ url }) =>
          String(url || '').trim() && !/^https?:\/\//i.test(normalizeUrl(url))
      );
      if (dropped.length) {
        toast(
          `${dropped.length} evidence entr${
            dropped.length === 1 ? 'y' : 'ies'
          } skipped (invalid URL). Use full http(s) links.`,
          { icon: '⚠️' }
        );
      }

      if (normalizedEvidence.length) {
        try {
          const evColRef = collection(db, `marketplace/${docRef.id}/evidence`);
          await Promise.all(
            normalizedEvidence.map(({ type, url }) =>
              addDoc(evColRef, {
                value: url,
                type: type || 'Other',
                status: 'pending',
                updatedAt: Timestamp.now(),
              })
            )
          );
        } catch (e) {
          console.error('Evidence save failed:', e);
          toast.error(
            'Unable to save evidence links. Check your connection or permissions.'
          );
        }
      }

      // _meta should include 'evidence' if any were saved
      const metaSubs = ['breeds', 'geography', 'tags'];
      if (normalizedEvidence.length) metaSubs.push('evidence');
      await writeSubsMeta(db, `marketplace/${docRef.id}`, metaSubs);

      // Update UI
      setProducts((prev) => [
        {
          id: docRef.id,
          ...rootDoc,
          categoryName: catMap[selectedCategory]?.name || '',
          subCategoryName: catMap[selectedSubCategory]?.name || '',
          breeds: breedsToSave,
          geography: geoToSave,
          tags: tagsToSave,
        },
        ...prev,
      ]);

      toast.success('Product added successfully.');
    } catch (err) {
      console.error(err);
      toast.error('Error adding product.');
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Delete (dynamic via _meta) ---------- */
  const handleDeleteProduct = async (productId, productImageUrl) => {
    setLoading(true);
    try {
      if (productImageUrl) {
        try {
          const imgRef = storageRef(storage, productImageUrl);
          await deleteObject(imgRef);
        } catch {}
      }

      const metaSubs = await readSubsMetaNames(db, productId);
      const subs =
        metaSubs && Array.isArray(metaSubs) && metaSubs.length
          ? [...metaSubs]
          : ['breeds', 'geography', 'tags'];
      subs.push('_meta');

      await Promise.all(
        subs.map((name) => deleteSubcollection(db, productId, name))
      );

      await deleteDoc(doc(db, 'marketplace', productId));

      setProducts((prev) => prev.filter((p) => p.id !== productId));
      toast.success('Product and related data removed.');
    } catch (err) {
      console.error(err);
      toast.error('Error deleting product and related data.');
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Step content ---------- */

  const Step1Basics = () => {
    const subCategories = subCategoriesFor(selectedCategory);
    return (
      <div className='row g-3 mt-5'>
        {/* Row Line */}
        <div className='mb-3 row'>
          {/* Product */}
          <label className='col-lg-2 col-form-label'>
            Product Name
            <span className='text-info'>*</span>
          </label>
          <div className='col-lg-3'>
            <input
              data-focus-id='product-name'
              className='form-control'
              placeholder='Enter a product name..'
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            />
          </div>

          {/* Subtitle */}
          <label className='col-lg-2 col-form-label'>Short Tagline</label>
          <div className='col-lg-3'>
            <input
              data-focus-id='product-subtitle'
              className='form-control'
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder='e.g., Warm waterproof winter coat'
            />
          </div>
        </div>

        {/* Row Line */}
        <div className='mb-3 row'>
          {/* Brand */}
          <label className='col-lg-2 col-form-label'>
            Product Brand
            <span className='text-info'>*</span>
          </label>
          <div className='col-lg-3'>
            <input
              data-focus-id='product-brand'
              className='form-control'
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder='Enter a product Brand..'
            />
          </div>
        </div>

        {/* Row Line */}
        <div className='mb-3 row'>
          {/* Category */}
          <label className='col-lg-2 col-form-label'>
            Product Category
            <span className='text-info'>*</span>
          </label>
          <div className='col-lg-3 form-floating'>
            <select
              className='form-select native-select'
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value=''>-- Choose --</option>
              {categories
                .filter((c) => !c.parent)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
            <label className='form-label'>
              &nbsp;&nbsp;&nbsp;&nbsp;Category
            </label>
          </div>

          {/* Subcategory */}
          <label className='col-lg-2 col-form-label'>
            Sub Category
            <span className='text-info'>*</span>
          </label>
          <div className='col-lg-3 form-floating'>
            <select
              className='form-select native-select'
              value={selectedSubCategory}
              onChange={(e) => setSelectedSubCategory(e.target.value)}
              disabled={!selectedCategory}
            >
              <option value=''>Select Sub Category</option>
              {subCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <label className='form-label'>
              &nbsp;&nbsp;&nbsp;&nbsp;Sub Category
            </label>
          </div>
        </div>
      </div>
    );
  };

  const Step2Targeting = () => (
    <div className='row g-3'>
      {/* Dog Breeds (searchable multi-select) */}
      <div className='col-md-12'>
        <label className='form-label d-flex justify-content-between'>
          <span>Dog Breeds</span>
          <button
            type='button'
            className='btn btn-link btn-sm p-0'
            onClick={() => setSelectedBreeds([])}
          >
            Clear
          </button>
        </label>

        <SearchableMultiSelect
          options={[...BREEDS]}
          values={selectedBreeds}
          onChange={(arr) =>
            arr.includes(BREED_ALL)
              ? setSelectedBreeds([BREED_ALL])
              : setSelectedBreeds(arr)
          }
          showAllOption
          allValue={BREED_ALL}
          allLabel='All Breeds'
          placeholder='Search breeds…'
        />

        {/* Hidden native select fallback */}
        <select
          className='form-select d-none'
          multiple
          value={selectedBreeds}
          readOnly
        >
          <option value={BREED_ALL}>All Breeds</option>
          {BREEDS.map((b) => (
            <option key={b.value} value={b.value}>
              {b.label}
            </option>
          ))}
        </select>
      </div>

      {/* Geography (checkboxes) */}
      <div className='col-12'>
        <label className='form-label'>
          Geography (where this is advertised)
        </label>
        <div className='row'>
          {GEO_OPTIONS.map((g, idx) => {
            const id = `geo-${idx}`;
            return (
              <div key={g} className='col-xl-6 col-xxl-6 col-6'>
                <div className='form-check custom-checkbox mb-3'>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    id={id}
                    checked={selectedGeo.includes(g)}
                    onChange={() => toggleGeo(g)}
                  />
                  <label className='form-check-label' htmlFor={id}>
                    {g}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tags (optional) */}
      <div className='col-12'>
        <label className='form-label'>Tags (optional)</label>
        <TagInput id='productTags' tags={tags} onChange={setTags} />
      </div>
    </div>
  );

  const Step3PricingLinks = () => (
    <div className='row g-3'>
      {/* Price */}
      <div className='col-md-6'>
        <label className='form-label'>Price</label>
        <input
          data-focus-id='product-price'
          type='number'
          step='0.01'
          className='form-control'
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder='199.99'
        />
      </div>
      {/* External URL */}
      <div className='col-md-6'>
        <label className='form-label'>External Product URL</label>
        <input
          data-focus-id='product-external-url'
          className='form-control'
          value={externalUrl}
          onChange={(e) => setExternalUrl(e.target.value)}
          placeholder='https://partner-site.com/product'
        />
      </div>
      {/* Description */}
      <div className='col-12'>
        <label className='form-label'>Description</label>
        <textarea
          data-focus-id='product-description'
          className='form-control'
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </div>
  );

  const Step4MediaEvidence = () => (
    <div className='row g-3'>
      {/* Evidence rows */}
      <div className='col-12'>
        <label className='form-label d-block'>Evidence (optional)</label>

        {evidenceRows.map((row) => (
          <div key={row.id} className='row align-items-center g-2 mb-2'>
            {/* Right-aligned dropdown */}
            <div className='col-md-4 ms-auto text-end form-floating'>
              <select
                className='form-select native-select'
                value={row.type}
                onChange={(e) =>
                  updateEvidenceRow(row.id, { type: e.target.value })
                }
              >
                <option value=''>Select evidence type…</option>
                {EVIDENCE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <label className='form-check-label'>Evidence Type</label>
            </div>

            {/* URL input */}
            <div className='col-md-7'>
              <input
                data-focus-id={`evidence-url-${row.id}`}
                className='form-control'
                placeholder='https://link-to-proof.example'
                value={row.url}
                onChange={(e) =>
                  updateEvidenceRow(row.id, { url: e.target.value })
                }
              />
            </div>

            {/* Remove button */}
            <div className='col-md-1 d-flex justify-content-center'>
              {evidenceRows.length > 1 && (
                <button
                  type='button'
                  className='btn btn-outline-danger'
                  title='Remove'
                  onClick={() => removeEvidenceRow(row.id)}
                >
                  <i className='fas fa-trash' />
                </button>
              )}
            </div>
          </div>
        ))}

        <div className='mt-2'>
          <button
            type='button'
            className='btn btn-outline-secondary btn-sm'
            onClick={addEvidenceRow}
          >
            + Add another evidence
          </button>
        </div>
      </div>

      {/* Uploader — keep Dropzone here; rendered once and frozen to preserve preview */}
      <div className='col-12'>
        <label className='form-label'>Main Product Image</label>
        <ImageUploader
          ref={imageUploaderRef}
          uid={globalData.uid}
          onFileSelect={() => {}}
          onUpload={() => {}}
        />
      </div>
    </div>
  );

  /* ---------- Wizard header ---------- */
  const WizardHeader = () => {
    const steps = [
      { n: 1, label: 'Basics' },
      { n: 2, label: 'Targeting' },
      { n: 3, label: 'Pricing & Links' },
      { n: 4, label: 'Media & Evidence' },
    ];
    return (
      <div className='mb-3'>
        <div className='d-flex align-items-center justify-content-between'>
          {steps.map((s) => (
            <div
              key={s.n}
              className={`d-flex align-items-center ${
                s.n < maxStep ? 'flex-grow-1 me-2' : ''
              }`}
            >
              <div
                className='rounded-circle d-flex align-items-center justify-content-center'
                style={{
                  width: 32,
                  height: 32,
                  background: step >= s.n ? '#4b7cf3' : '#e9ecef',
                  color: step >= s.n ? '#fff' : '#6c757d',
                  fontWeight: 600,
                }}
              >
                {s.n}
              </div>
              <div className='ms-2 me-2 small'>{s.label}</div>
              {s.n < maxStep && (
                <div
                  className='flex-grow-1'
                  style={{
                    height: 4,
                    background: step > s.n ? '#4b7cf3' : '#e9ecef',
                    borderRadius: 2,
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  /* ---------- Modal body (wizard) ---------- */
  const renderWizardBody = () => (
    <div ref={focus.containerRef} onFocusCapture={focus.onFocusCapture}>
      <WizardHeader />
      {step === 1 && <Step1Basics />}
      {step === 2 && <Step2Targeting />}
      {step === 3 && <Step3PricingLinks />}
      {step === 4 && <Step4MediaEvidence />}

      <div className='d-flex justify-content-between mt-3'>
        <button
          type='button'
          className='btn btn-light'
          onClick={goBack}
          disabled={step === 1}
        >
          Back
        </button>
        {step < maxStep ? (
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleNext}
          >
            Next
          </button>
        ) : (
          <span />
        )}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className='col-xl-12 col-lg-4'>
        <div className='row'>
          {/* Left */}
          <div className='col-xl-3 col-lg-4'>
            <div className='card'>
              <div className='card-header bg-transparent d-flex justify-content-between align-items-center'>
                <h5 className='mb-0'>Product Categories</h5>
                <button
                  type='button'
                  className='btn btn-primary btn-xs'
                  data-bs-toggle='modal'
                  data-bs-target='#addProduct'
                  onClick={handleOpenAdd}
                >
                  <i className='fa fa-upload font-12 align-middle me-2'></i>
                  Add Product
                </button>
              </div>
              <div className='card-body'>
                <MarketplaceMenu
                  globalUser={globalUser}
                  categories={categoryTree}
                  onCategorySelect={(categoryId) => {
                    fetchProducts(categoryId);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Right */}
          <div className='col-xl-9 col-lg-8'>
            {loading && products.length === 0 ? (
              <p>Loading products…</p>
            ) : products.length === 0 ? (
              <div className='col-sm-6 col-md-4'>
                <div className='alert alert-danger left-icon-big alert-dismissible fade show'>
                  <button
                    type='button'
                    className='btn-close'
                    data-bs-dismiss='alert'
                    aria-label='btn-close'
                  >
                    <span>
                      <i className='mdi mdi-btn-close'></i>
                    </span>
                  </button>
                  <div className='media'>
                    <div className='alert-left-icon-big'>
                      <span>
                        <i className='mdi mdi-help-circle-outline'></i>
                      </span>
                    </div>
                    <div className='media-body'>
                      <h5 className='mt-1 mb-2'>No Products!</h5>
                      <p className='mb-0'>You have no products to show.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {products.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onRequestDelete={({ id, avatar, product: name }) => {
                      setDeleteId(id);
                      setDeleteAvatar(avatar);
                      setDeleteName(name || 'this product');
                    }}
                  />
                ))}

                {lastVisible && (
                  <div className='d-grid'>
                    <button
                      className='btn btn-outline-primary'
                      onClick={fetchMore}
                    >
                      Load more
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add Product Modal (wizard) */}
      <Modal
        id='addProduct'
        title='Add Product'
        onConfirm={async () => {
          setIsSaving(true);
          try {
            await handleSaveProduct();
          } finally {
            setIsSaving(false);
          }
        }}
        confirmText={isSaving ? 'Saving…' : 'Save'}
        loading={isSaving}
        size='xl'
      >
        {renderWizardBody()}
      </Modal>

      {/* Confirm Delete */}
      <Modal
        id='confirmDelete'
        title='Remove This Product?'
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await handleDeleteProduct(deleteId, deleteAvatar);
          } finally {
            setIsDeleting(false);
          }
        }}
        confirmText='Yes, delete'
        cancelText='Cancel'
        loading={isDeleting}
        loadingText='Deleting...'
        size='medium'
      >
        <p>
          Are you sure you want to delete <strong>{deleteName}</strong>?
        </p>
      </Modal>
    </Layout>
  );
}

export default Products;
